import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Loader2, TrendingUp, Truck, Users, Calendar, Plus, CheckCircle2 } from 'lucide-react';

const FleetOverview = () => {
  const { API } = useAuth();
  const [stats, setStats] = useState(null);
  const [allotments, setAllotments] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [selectedAllotment, setSelectedAllotment] = useState(null);
  const [formData, setFormData] = useState({
    driver_id: '',
    vehicle_id: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, allotmentsRes, driversRes, vehiclesRes, historyRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/allotments?status=active`),
        axios.get(`${API}/drivers`),
        axios.get(`${API}/vehicles`),
        axios.get(`${API}/allotments/history`)
      ]);
      setStats(statsRes.data);
      setAllotments(allotmentsRes.data);
      setDrivers(driversRes.data);
      setVehicles(vehiclesRes.data.filter(v => !v.is_allotted));
      setHistory(historyRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleAllotVehicle = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/allotments`, formData);
      toast.success('Vehicle allotted successfully');
      setDialogOpen(false);
      setFormData({ driver_id: '', vehicle_id: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to allot vehicle');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitVehicle = async () => {
    if (!selectedAllotment) return;
    setSubmitting(true);

    try {
      await axios.patch(`${API}/allotments/${selectedAllotment.id}/submit`, {
        submission_date: new Date().toISOString()
      });
      toast.success('Vehicle submitted successfully');
      setSubmitDialogOpen(false);
      setSelectedAllotment(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to submit vehicle');
    } finally {
      setSubmitting(false);
    }
  };

  const getDriverName = (driverId) => {
    const driver = drivers.find(d => d.id === driverId);
    return driver ? driver.name : 'Unknown';
  };

  const getVehicleNumber = (vehicleId) => {
    const allVehicles = [...vehicles, ...allotments.map(a => ({ id: a.vehicle_id }))];  
    const vehicle = allVehicles.find(v => v.id === vehicleId);
    return vehicle?.vehicle_number || vehicleId;
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="fleet-overview-section">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="card-hover">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Drivers</p>
                <p className="text-3xl font-bold">{stats?.total_drivers || 0}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-lg">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Vehicles</p>
                <p className="text-3xl font-bold">{stats?.total_vehicles || 0}</p>
              </div>
              <div className="bg-green-100 p-3 rounded-lg">
                <Truck className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Active Allotments</p>
                <p className="text-3xl font-bold">{stats?.active_allotments || 0}</p>
              </div>
              <div className="bg-orange-100 p-3 rounded-lg">
                <Calendar className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Available Vehicles</p>
                <p className="text-3xl font-bold">{stats?.available_vehicles || 0}</p>
              </div>
              <div className="bg-purple-100 p-3 rounded-lg">
                <TrendingUp className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Active Allotments */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Active Vehicle Allotments</CardTitle>
              <CardDescription>Current vehicle assignments to drivers</CardDescription>
            </div>
            <Button
              className="btn-primary"
              onClick={() => setDialogOpen(true)}
              data-testid="allot-vehicle-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Allot Vehicle
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Table data-testid="active-allotments-table">
            <TableHeader>
              <TableRow className="table-header">
                <TableHead>Allotment ID</TableHead>
                <TableHead>Driver</TableHead>
                <TableHead>Vehicle</TableHead>
                <TableHead>Allotment Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {allotments.map((allotment) => (
                <TableRow key={allotment.id} className="table-row">
                  <TableCell className="font-mono">{allotment.allotment_id}</TableCell>
                  <TableCell className="font-medium">{getDriverName(allotment.driver_id)}</TableCell>
                  <TableCell className="font-mono">{getVehicleNumber(allotment.vehicle_id)}</TableCell>
                  <TableCell>{new Date(allotment.allotment_date).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <span className="status-badge status-active">Active</span>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setSelectedAllotment(allotment);
                        setSubmitDialogOpen(true);
                      }}
                      data-testid={`submit-vehicle-${allotment.id}`}
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Submit
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {allotments.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                    No active allotments
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Allotment History */}
      <Card>
        <CardHeader>
          <CardTitle>Allotment History</CardTitle>
          <CardDescription>Complete audit trail for billing and tracking</CardDescription>
        </CardHeader>
        <CardContent>
          <Table data-testid="allotment-history-table">
            <TableHeader>
              <TableRow className="table-header">
                <TableHead>Allotment ID</TableHead>
                <TableHead>Driver</TableHead>
                <TableHead>Vehicle</TableHead>
                <TableHead>Allotment Date</TableHead>
                <TableHead>Submission Date</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {history.slice(0, 10).map((record) => (
                <TableRow key={record.id} className="table-row">
                  <TableCell className="font-mono">{record.allotment_id}</TableCell>
                  <TableCell className="font-medium">{getDriverName(record.driver_id)}</TableCell>
                  <TableCell className="font-mono">{getVehicleNumber(record.vehicle_id)}</TableCell>
                  <TableCell>{new Date(record.allotment_date).toLocaleDateString()}</TableCell>
                  <TableCell>
                    {record.submission_date
                      ? new Date(record.submission_date).toLocaleDateString()
                      : '-'}
                  </TableCell>
                  <TableCell>
                    <span
                      className={`status-badge ${
                        record.status === 'active' ? 'status-active' : 'status-completed'
                      }`}
                    >
                      {record.status}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
              {history.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                    No history records
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Allot Vehicle Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="allot-vehicle-dialog">
          <DialogHeader>
            <DialogTitle>Allot Vehicle to Driver</DialogTitle>
            <DialogDescription>Assign an available vehicle to a driver</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAllotVehicle} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="driver_id">Select Driver</Label>
              <select
                id="driver_id"
                value={formData.driver_id}
                onChange={(e) => setFormData({ ...formData, driver_id: e.target.value })}
                required
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm input-focus"
                data-testid="allot-driver-select"
              >
                <option value="">Select a driver</option>
                {drivers.map((driver) => (
                  <option key={driver.id} value={driver.id}>
                    {driver.name} ({driver.driver_id})
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="vehicle_id">Select Vehicle</Label>
              <select
                id="vehicle_id"
                value={formData.vehicle_id}
                onChange={(e) => setFormData({ ...formData, vehicle_id: e.target.value })}
                required
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm input-focus"
                data-testid="allot-vehicle-select"
              >
                <option value="">Select a vehicle</option>
                {vehicles.map((vehicle) => (
                  <option key={vehicle.id} value={vehicle.id}>
                    {vehicle.vehicle_number} - {vehicle.make} {vehicle.model}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes">Notes (Optional)</Label>
              <textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm input-focus"
                placeholder="Any additional notes..."
                data-testid="allot-notes-input"
              />
            </div>
            <Button type="submit" className="w-full btn-primary" disabled={submitting} data-testid="submit-allotment-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Allot Vehicle
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Submit Vehicle Dialog */}
      <Dialog open={submitDialogOpen} onOpenChange={setSubmitDialogOpen}>
        <DialogContent data-testid="submit-vehicle-dialog">
          <DialogHeader>
            <DialogTitle>Submit Vehicle</DialogTitle>
            <DialogDescription>
              Mark this vehicle as returned. This action will be recorded in the audit trail.
            </DialogDescription>
          </DialogHeader>
          {selectedAllotment && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Allotment ID:</span>
                  <span className="font-mono font-semibold">{selectedAllotment.allotment_id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Driver:</span>
                  <span className="font-medium">{getDriverName(selectedAllotment.driver_id)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Vehicle:</span>
                  <span className="font-mono">{getVehicleNumber(selectedAllotment.vehicle_id)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Allotment Date:</span>
                  <span>{new Date(selectedAllotment.allotment_date).toLocaleDateString()}</span>
                </div>
              </div>
              <Button
                className="w-full btn-primary"
                onClick={handleSubmitVehicle}
                disabled={submitting}
                data-testid="confirm-submit-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Confirm Submission
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FleetOverview;
